/**
 * MBA Lab — pure state model for the Listening Session Facilitator. No DOM,
 * storage, or analytics dependencies - listening-session-facilitator.js owns
 * all of that.
 *
 * Every field here is real, potentially sensitive free text about a specific
 * real conversation - unlike the planner (whose opt-in "remember" tier keeps
 * genuinely non-sensitive structural/progress state - mode, safety flags,
 * rubric checks - even after stripping draft text), this tool has no
 * non-sensitive residual state: every field IS the draft text. Offering an
 * opt-in "remember" tier here would mean a checkbox that silently persists
 * nothing, which is worse than not offering it. So persistence is
 * session-only, full stop - see this tool's UI file - and buildReflectionText()
 * below exists so a user can explicitly copy their own reflection out to
 * wherever they choose, rather than MBA Lab holding onto it for them.
 */

import { PREPARE_FIELDS, REFLECT_FIELDS } from './listening-prompts.js';

export const SCHEMA_NAME = 'mbalab.listening-session-facilitator';
export const SCHEMA_VERSION = 1;

export const LIMITS = Object.freeze({
  fieldLength: 800,
  maxSerializedBytes: 15 * 1024,
});

const ALL_FIELDS = [...PREPARE_FIELDS, ...REFLECT_FIELDS];
const TEXT_FIELD_LIMITS = Object.freeze(
  Object.fromEntries(ALL_FIELDS.map((f) => [f.key, LIMITS.fieldLength]))
);
export const TOTAL_FIELDS = ALL_FIELDS.length;

function clampText(value, maxLen) {
  return typeof value === 'string' ? value.slice(0, maxLen) : '';
}

export function createInitialState() {
  const state = { schema: SCHEMA_NAME, version: SCHEMA_VERSION, savedAt: null };
  for (const key of Object.keys(TEXT_FIELD_LIMITS)) state[key] = '';
  return state;
}

export function setField(state, field, value) {
  if (!Object.hasOwn(TEXT_FIELD_LIMITS, field)) return state;
  return { ...state, [field]: clampText(value, TEXT_FIELD_LIMITS[field]) };
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const state = {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
  };
  for (const [key, maxLen] of Object.entries(TEXT_FIELD_LIMITS)) state[key] = clampText(r[key], maxLen);
  return state;
}

const MIGRATIONS = {
  // No prior schema version has ever shipped - see charter-model.js's
  // identical note. Proves the mechanism works before it's ever needed.
  0: (state) => ({ ...state, version: 1 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

export function countAnswered(state) {
  return Object.keys(TEXT_FIELD_LIMITS).filter((key) => state[key].trim() !== '').length;
}

export function hasAnyContent(state) {
  return countAnswered(state) > 0;
}

/** Plain text, not markdown - this is a personal export, never rendered back
 * through any markdown pipeline, so there's nothing to escape against.
 * Skips any field the user left blank rather than printing an empty label. */
export function buildReflectionText(state) {
  if (!hasAnyContent(state)) return '';

  const lines = ['Your listening session notes', ''];
  for (const field of PREPARE_FIELDS) {
    if (!state[field.key].trim()) continue;
    lines.push(field.label, state[field.key], '');
  }
  for (const field of REFLECT_FIELDS) {
    if (!state[field.key].trim()) continue;
    lines.push(field.label, state[field.key], '');
  }
  lines.push('Built with the free MBA Lab listening session facilitator - mbalab.net/tools/listening-session-facilitator');
  return lines.join('\n');
}
